<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import Swal from "sweetalert2";
import { clearCompanySettingsCache, setCompanySettingsCache } from "@/services/companySettings";

const store = useStore();
const canEditCompany = computed(() => store.getters.hasPermission('settings.manage'));
const canViewCompany = computed(() => store.getters.hasPermission('settings.manage'));

// Form data
const formData = ref({
  CompanyName: '',
  DisplayName: '',
  Logo: '',
  LogoUrl: '',
  AddressLine1: '',
  AddressLine2: '',
  City: '',
  State: '',
  StateCode: '',
  PinCode: '',
  Phone1: '',
  Email: '',
  GSTIN: '',
  GSTType: 'REGULAR',
  PANNo: '',
  DrugLicenseNo: '',
  BankName: '',
  AccountNo: '',
  IFSCCode: '',
  UPIId: '',
  InvoicePrefix: 'INV',
  PurchasePrefix: 'PUR',
  InvoiceFooter: '',
  InvoiceTerms: '',
  MultiLocationEnabled: false,
  MaxLocations: 1,
  DefaultLocationId: '',
  LocationMode: 'SINGLE'
});

const loading = ref(false);
const saving = ref(false);
const logoUploading = ref(false);
const logoDeleting = ref(false);
const logoPreview = ref(null);
const locations = ref([]);
const savingLocation = ref(false);
const editingLocationId = ref('');
const locationForm = ref({
  locationid: '',
  locationname: '',
  locationtype: 'WAREHOUSE',
  address: '',
  is_active: true,
  is_default: false
});

// GST Type options
const gstTypeOptions = [
  { value: 'REGULAR', label: 'Regular' },
  { value: 'COMPOSITION', label: 'Composition' },
  { value: 'UNREGISTERED', label: 'Unregistered' }
];

const locationModeOptions = [
  { value: 'SINGLE', label: 'Single Location' },
  { value: 'MULTI', label: 'Multi Location' },
  { value: 'ZONE', label: 'Zone Wise' }
];

const locationTypeOptions = [
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'STORE', label: 'Store' },
  { value: 'GODOWN', label: 'Godown' },
  { value: 'COUNTER', label: 'Counter' }
];

// Indian states list
const indianStates = [
  { code: '01', name: 'Jammu and Kashmir' },
  { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' },
  { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' },
  { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' },
  { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' },
  { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' },
  { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' },
  { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' },
  { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' },
  { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' },
  { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' },
  { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' },
  { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman and Diu' },
  { code: '26', name: 'Dadra and Nagar Haveli' },
  { code: '27', name: 'Maharashtra' },
  { code: '28', name: 'Andhra Pradesh' },
  { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' },
  { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' },
  { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' },
  { code: '35', name: 'Andaman and Nicobar Islands' },
  { code: '36', name: 'Telangana' },
  { code: '37', name: 'Ladakh' }
];

const activeLocations = computed(() => locations.value.filter(location => location.is_active));
const maxLocationsReached = computed(() => locations.value.length >= Number(formData.value.MaxLocations || 1));
const canCreateMoreLocations = computed(() => !editingLocationId.value && !maxLocationsReached.value);
const displayLogoUrl = computed(() => logoPreview.value || formData.value.LogoUrl || null);

// Methods
const loadCompanySettings = async () => {
  loading.value = true;
  try {
    const response = await axios.get('/v1/company-settings');
    if (response.data) {
      Object.assign(formData.value, response.data);
      setCompanySettingsCache(response.data);
    }
  } catch (error) {
    console.error('Error loading company settings:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to load company settings'
    });
  } finally {
    loading.value = false;
  }
};

const loadLocations = async () => {
  try {
    const response = await axios.get('/v1/locations', { params: { include_inactive: true } });
    locations.value = response.data || [];
    if (!formData.value.DefaultLocationId) {
      const defaultLocation = locations.value.find(location => location.is_default);
      if (defaultLocation) {
        formData.value.DefaultLocationId = defaultLocation.locationid;
      }
    }
  } catch (error) {
    console.error('Error loading locations:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to load locations'
    });
  }
};

const saveCompanySettings = async () => {
  if (!canEditCompany.value) {
    Swal.fire({
      icon: 'warning',
      title: 'Access Denied',
      text: 'You do not have permission to edit company settings'
    });
    return;
  }

  // Validate required fields
  if (!formData.value.CompanyName || !formData.value.AddressLine1 || 
      !formData.value.City || !formData.value.State || !formData.value.StateCode ||
      !formData.value.PinCode || !formData.value.Phone1 || !formData.value.GSTIN) {
    Swal.fire({
      icon: 'warning',
      title: 'Validation Error',
      text: 'Please fill in all required fields'
    });
    return;
  }

  // Validate GSTIN format
  if (!validateGSTIN(formData.value.GSTIN)) {
    Swal.fire({
      icon: 'warning',
      title: 'Validation Error',
      text: 'Please enter a valid GSTIN (15 characters alphanumeric)'
    });
    return;
  }

  saving.value = true;
  try {
    const payload = {
      ...formData.value,
      DefaultLocation: undefined,
      Logo: undefined,
      LogoUrl: undefined
    };
    const response = await axios.put('/v1/company-settings', payload);
    
    if (response.data.success) {
      const refreshed = await axios.get('/v1/company-settings');
      Object.assign(formData.value, refreshed.data || {});
      setCompanySettingsCache(refreshed.data || {});
      store.commit('SET_COMPANY_SETTINGS', refreshed.data || {});
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Company settings saved successfully'
      });
    }
  } catch (error) {
    console.error('Error saving company settings:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.response?.data?.detail || 'Failed to save company settings'
    });
  } finally {
    saving.value = false;
  }
};

const resetLocationForm = () => {
  editingLocationId.value = '';
  locationForm.value = {
    locationid: '',
    locationname: '',
    locationtype: 'WAREHOUSE',
    address: '',
    is_active: true,
    is_default: false
  };
};

const editLocation = (location) => {
  editingLocationId.value = location.locationid;
  locationForm.value = {
    locationid: location.locationid,
    locationname: location.locationname,
    locationtype: location.locationtype,
    address: location.address || '',
    is_active: !!location.is_active,
    is_default: !!location.is_default
  };
};

const saveLocation = async () => {
  const isEditing = !!editingLocationId.value;

  if (!locationForm.value.locationid || !locationForm.value.locationname) {
    Swal.fire({
      icon: 'warning',
      title: 'Validation Error',
      text: 'Location ID and location name are required'
    });
    return;
  }

  savingLocation.value = true;
  try {
    if (editingLocationId.value) {
      await axios.put(`/v1/locations/${editingLocationId.value}`, {
        locationname: locationForm.value.locationname,
        locationtype: locationForm.value.locationtype,
        address: locationForm.value.address,
        is_active: locationForm.value.is_active,
        is_default: locationForm.value.is_default
      });
    } else {
      await axios.post('/v1/locations', locationForm.value);
    }

    await loadLocations();

    if (locationForm.value.is_default) {
      formData.value.DefaultLocationId = locationForm.value.locationid;
    }

    resetLocationForm();
    Swal.fire({
      icon: 'success',
      title: 'Success',
      text: isEditing ? 'Location updated successfully' : 'Location created successfully'
    });
  } catch (error) {
    console.error('Error saving location:', error);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.response?.data?.detail || 'Failed to save location'
    });
  } finally {
    savingLocation.value = false;
  }
};

const validateGSTIN = (gstin) => {
  if (!gstin) return false;
  if (gstin.length !== 15) return false;
  if (!/^[A-Z0-9]+$/.test(gstin)) return false;
  return true;
};

const handleStateChange = () => {
  const selectedState = indianStates.find(state => state.name === formData.value.State);
  if (selectedState) {
    formData.value.StateCode = selectedState.code;
  }
};

const handleLogoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
  if (!allowedTypes.includes(file.type)) {
    Swal.fire({ icon: "warning", title: "Invalid file", text: "Please upload a PNG, JPG, JPEG, or WEBP image." });
    event.target.value = "";
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    Swal.fire({ icon: "warning", title: "File too large", text: "Logo must be 2 MB or smaller." });
    event.target.value = "";
    return;
  }

  logoPreview.value = URL.createObjectURL(file);
  logoUploading.value = true;
  try {
    const body = new FormData();
    body.append("file", file);
    const response = await axios.post("/v1/company-settings/logo", body, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    formData.value.Logo = response.data.Logo || "";
    formData.value.LogoUrl = response.data.LogoUrl || "";
    logoPreview.value = null;
    clearCompanySettingsCache();
    const refreshed = await axios.get('/v1/company-settings');
    Object.assign(formData.value, refreshed.data || {});
    setCompanySettingsCache(refreshed.data || {});
    store.commit('SET_COMPANY_SETTINGS', refreshed.data || {});
    Swal.fire({ icon: "success", title: "Success", text: "Company logo uploaded successfully" });
  } catch (error) {
    console.error("Error uploading logo:", error);
    logoPreview.value = null;
    Swal.fire({
      icon: "error",
      title: "Upload failed",
      text: error.response?.data?.detail || "Failed to upload company logo",
    });
  } finally {
    logoUploading.value = false;
    event.target.value = "";
  }
};

const removeLogo = async () => {
  if (!canEditCompany.value || logoDeleting.value) return;
  logoDeleting.value = true;
  try {
    await axios.delete("/v1/company-settings/logo");
    formData.value.Logo = "";
    formData.value.LogoUrl = "";
    logoPreview.value = null;
    clearCompanySettingsCache();
    const refreshed = await axios.get('/v1/company-settings');
    Object.assign(formData.value, refreshed.data || {});
    setCompanySettingsCache(refreshed.data || {});
    store.commit('SET_COMPANY_SETTINGS', refreshed.data || {});
    Swal.fire({ icon: "success", title: "Success", text: "Company logo removed successfully" });
  } catch (error) {
    console.error("Error deleting logo:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.response?.data?.detail || "Failed to remove company logo",
    });
  } finally {
    logoDeleting.value = false;
  }
};

// Lifecycle
onMounted(() => {
  Promise.all([loadCompanySettings(), loadLocations()]);
});
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader
          title="Company Settings"
          title1="Manage your company information and GST details"
          :items="[]"
        />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading company settings...</span>
            </div>

            <div v-else class="settings-form">
              <!-- Basic Information -->
              <div class="section">
                <h4 class="section-title">Basic Information</h4>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Company Name *</label>
                      <input 
                        v-model="formData.CompanyName" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Display Name</label>
                      <input 
                        v-model="formData.DisplayName" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <label>Logo</label>
                      <div class="logo-upload">
                        <div v-if="displayLogoUrl" class="logo-preview">
                          <img :src="displayLogoUrl" alt="Company Logo" />
                          <button
                            v-if="canEditCompany"
                            type="button"
                            class="btn-remove-logo"
                            @click="removeLogo"
                            :disabled="logoDeleting"
                          >
                            x
                          </button>
                        </div>
                        <div v-else class="logo-upload-area">
                          <input
                            v-if="canEditCompany"
                            type="file"
                            @change="handleLogoUpload"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            class="file-input"
                            id="logoInput"
                            :disabled="logoUploading"
                          />
                          <label for="logoInput" class="upload-label">
                            <div class="upload-icon">
                              <vue-feather type="upload" class="feather-20"></vue-feather>
                            </div>
                            <span>{{ logoUploading ? 'Uploading...' : 'Click to upload logo' }}</span>
                            <small>PNG, JPG, JPEG, or WEBP. Max 2 MB.</small>
                          </label>
                        </div>
                        <div v-if="displayLogoUrl && canEditCompany" class="logo-upload-actions">
                          <input
                            type="file"
                            @change="handleLogoUpload"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            class="file-input"
                            id="logoReplaceInput"
                            :disabled="logoUploading || logoDeleting"
                          />
                          <label for="logoReplaceInput" class="btn btn-outline-secondary mb-0">
                            {{ logoUploading ? 'Uploading...' : 'Replace Logo' }}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Address Information -->
              <div class="section">
                <h4 class="section-title">Address Information</h4>
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <label>Address Line 1 *</label>
                      <input 
                        v-model="formData.AddressLine1" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <label>Address Line 2</label>
                      <input 
                        v-model="formData.AddressLine2" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>City *</label>
                      <input 
                        v-model="formData.City" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group">
                      <label>State *</label>
                      <select 
                        v-model="formData.State" 
                        class="form-control"
                        @change="handleStateChange"
                        :disabled="!canEditCompany"
                      >
                        <option value="">Select State</option>
                        <option 
                          v-for="state in indianStates" 
                          :key="state.code" 
                          :value="state.name"
                        >
                          {{ state.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="form-group">
                      <label>State Code *</label>
                      <input 
                        v-model="formData.StateCode" 
                        type="text" 
                        class="form-control" 
                        maxlength="2"
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-2">
                    <div class="form-group">
                      <label>Pin Code *</label>
                      <input 
                        v-model="formData.PinCode" 
                        type="text" 
                        class="form-control" 
                        maxlength="10"
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact Information -->
              <div class="section">
                <h4 class="section-title">Contact Information</h4>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Phone 1 *</label>
                      <input 
                        v-model="formData.Phone1" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Email</label>
                      <input 
                        v-model="formData.Email" 
                        type="email" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- GST Information -->
              <div class="section">
                <h4 class="section-title">GST Information</h4>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>GSTIN *</label>
                      <input 
                        v-model="formData.GSTIN" 
                        type="text" 
                        class="form-control" 
                        maxlength="15"
                        placeholder="29XXXXXXXXXXXXXX"
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>GST Type *</label>
                      <select 
                        v-model="formData.GSTType" 
                        class="form-control"
                        :disabled="!canEditCompany"
                      >
                        <option 
                          v-for="option in gstTypeOptions" 
                          :key="option.value" 
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>PAN Number</label>
                      <input 
                        v-model="formData.PANNo" 
                        type="text" 
                        class="form-control" 
                        maxlength="10"
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Drug License Number (Pharmacy only)</label>
                      <input 
                        v-model="formData.DrugLicenseNo" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Bank Information -->
              <div class="section">
                <h4 class="section-title">Bank Information (for Invoice Print)</h4>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Bank Name</label>
                      <input 
                        v-model="formData.BankName" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Account Number</label>
                      <input 
                        v-model="formData.AccountNo" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>IFSC Code</label>
                      <input 
                        v-model="formData.IFSCCode" 
                        type="text" 
                        class="form-control" 
                        maxlength="11"
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>UPI ID</label>
                      <input 
                        v-model="formData.UPIId" 
                        type="text" 
                        class="form-control" 
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Invoice Settings -->
              <div class="section">
                <h4 class="section-title">Invoice Settings</h4>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Invoice Prefix</label>
                      <input 
                        v-model="formData.InvoicePrefix" 
                        type="text" 
                        class="form-control" 
                        maxlength="10"
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>Purchase Prefix</label>
                      <input 
                        v-model="formData.PurchasePrefix" 
                        type="text" 
                        class="form-control" 
                        maxlength="10"
                        :disabled="!canEditCompany"
                      />
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <label>Invoice Footer</label>
                      <textarea 
                        v-model="formData.InvoiceFooter" 
                        class="form-control" 
                        rows="3"
                        :disabled="!canEditCompany"
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-group">
                      <label>Invoice Terms & Conditions</label>
                      <textarea 
                        v-model="formData.InvoiceTerms" 
                        class="form-control" 
                        rows="3"
                        :disabled="!canEditCompany"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <div class="section">
                <div class="section-title-row">
                  <h4 class="section-title mb-0">Locations</h4>
                  <button
                    v-if="canEditCompany && canCreateMoreLocations"
                    type="button"
                    class="btn btn-outline-secondary"
                    @click="resetLocationForm"
                  >
                    New Location
                  </button>
                </div>

                <div v-if="maxLocationsReached && !editingLocationId" class="alert alert-info mb-3">
                  Maximum location limit reached. You can edit existing locations, but cannot add more.
                </div>

                <div v-if="editingLocationId || canCreateMoreLocations" class="location-editor">
                  <div class="row">
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Location ID *</label>
                        <input
                          v-model="locationForm.locationid"
                          type="text"
                          class="form-control"
                          :disabled="!canEditCompany || !!editingLocationId"
                        />
                      </div>
                    </div>
                    <div class="col-md-3">
                      <div class="form-group">
                        <label>Location Name *</label>
                        <input
                          v-model="locationForm.locationname"
                          type="text"
                          class="form-control"
                          :disabled="!canEditCompany"
                        />
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label>Address</label>
                        <input
                          v-model="locationForm.address"
                          type="text"
                          class="form-control"
                          :disabled="!canEditCompany"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="row align-items-end">
                    <div class="col-md-2">
                      <div class="form-group">
                        <label>Active</label>
                        <input v-model="locationForm.is_active" type="checkbox" :disabled="!canEditCompany" />
                      </div>
                    </div>
                    <div class="col-md-2">
                      <div class="form-group">
                        <label>Default</label>
                        <input v-model="locationForm.is_default" type="checkbox" :disabled="!canEditCompany" />
                      </div>
                    </div>
                    <div class="col-md-8">
                      <div class="location-actions">
                        <button
                          v-if="canEditCompany"
                          type="button"
                          class="btn btn-primary"
                          @click="saveLocation"
                          :disabled="savingLocation"
                        >
                          {{ savingLocation ? 'Saving...' : (editingLocationId ? 'Update Location' : 'Add Location') }}
                        </button>
                        <button
                          v-if="canEditCompany"
                          type="button"
                          class="btn btn-outline-secondary"
                          @click="resetLocationForm"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Address</th>
                        <th>Status</th>
                        <th>Default</th>
                        <th class="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="location in locations" :key="location.locationid">
                        <td>{{ location.locationid }}</td>
                        <td>{{ location.locationname }}</td>
                        <td>{{ location.address || '-' }}</td>
                        <td>{{ location.is_active ? 'Active' : 'Inactive' }}</td>
                        <td>{{ location.is_default ? 'Yes' : 'No' }}</td>
                        <td class="text-end">
                          <button
                            v-if="canEditCompany"
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            @click="editLocation(location)"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!locations.length">
                        <td colspan="6" class="text-center text-muted py-4">No locations added yet.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Save Button -->
              <div class="section">
                <div class="row">
                  <div class="col-md-12">
                    <button 
                      v-if="canEditCompany"
                      type="button" 
                      class="btn btn-primary"
                      @click="saveCompanySettings"
                      :disabled="saving"
                    >
                      <span v-if="saving">Saving...</span>
                      <span v-else>Save Settings</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loading-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  gap: 10px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #f3f4f6;
  border-top-color: #fb923c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.settings-form {
  max-width: 100%;
}

.section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f3f4f6;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #374151;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #fb923c;
  box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.1);
}

.form-control:disabled {
  background-color: #f9fafb;
  color: #6b7280;
  cursor: not-allowed;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #fb923c;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #f97316;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline-secondary {
  background: white;
  color: #475569;
  border: 1px solid #cbd5e1;
}

.btn-outline-secondary:hover:not(:disabled) {
  background: #f8fafc;
}

.switch-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
}

.switch-help {
  color: #6b7280;
  font-size: 13px;
}

.logo-upload {
  margin-top: 10px;
}

.logo-preview {
  position: relative;
  display: inline-block;
}

.logo-preview img {
  max-width: 200px;
  max-height: 150px;
  object-fit: contain;
  border: 1px solid #d1d5db;
  border-radius: 6px;
}

.btn-remove-logo {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
}

.logo-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 6px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
}

.logo-upload-area:hover {
  border-color: #fb923c;
}

.file-input {
  display: none;
}

.upload-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.upload-icon {
  font-size: 24px;
}

.upload-label span {
  color: #6b7280;
  font-size: 14px;
}

.upload-label small {
  color: #94a3b8;
  font-size: 12px;
}

.logo-upload-actions {
  margin-top: 12px;
}

.location-editor {
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fffaf5;
}

.location-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
</style>
